import { toConsentDashboardUrl } from 'config/consent-dashboard';

// When this tab last handed sign-in off. sessionStorage is shared by the consent
// dashboard page and this app's iframe (same origin), and survives the reload.
export const HANDOFF_AT_KEY = 'si-sign-in-handoff-at';
// A 401 again this soon after a hand-off means signing in didn't give the API a session
// it accepts; reloading again would loop.
export const HANDOFF_COOLDOWN_MS = 60 * 1000;

let handedOff = false;

function handedOffRecently(win: Window): boolean {
  try {
    const last = Number(win.sessionStorage.getItem(HANDOFF_AT_KEY));
    if (last && Date.now() - last < HANDOFF_COOLDOWN_MS) {
      return true;
    }
    win.sessionStorage.setItem(HANDOFF_AT_KEY, String(Date.now()));
  } catch {}
  return false;
}

/**
 * Sends the user to sign in after the API rejects the session (401).
 *
 * The consent dashboard serves this app on its own origin and owns sign-in: its
 * middleware picks the provider (Cognito or Kinde) and returns the user to the page
 * they asked for. So reload the page the user sees, which is the top window when this
 * app is embedded in the consent dashboard's iframe: signing in inside the iframe would
 * leave the login stuck in the frame, and Kinde's pages refuse to be framed. On this
 * app's own hostname there is no consent dashboard middleware, so go to the consent
 * dashboard instead.
 *
 * Runs at most once per page load, as several requests can fail at once, and not again
 * within HANDOFF_COOLDOWN_MS of the last hand-off, so a session the API keeps rejecting
 * doesn't reload the page forever.
 */
export function handSignInToConsentDashboard(win: Window = window): void {
  if (handedOff) {
    return;
  }
  handedOff = true;

  if (handedOffRecently(win)) {
    // eslint-disable-next-line no-console
    console.error('Still unauthenticated right after signing in; not redirecting again.');
    return;
  }

  const consentDashboardUrl = toConsentDashboardUrl(win.location.href);
  if (consentDashboardUrl !== win.location.href) {
    win.location.replace(consentDashboardUrl);
    return;
  }

  try {
    win.top!.location.reload();
  } catch {
    // The top window isn't ours to reload (embedded by another origin).
    win.location.reload();
  }
}
